/**
 * The handouts drawer's way into the GM's asset library (plan
 * assets-library, stage 3): **From my library…** posts an image or a
 * handout as a NEW handout - its bytes copied in, the session's quota
 * applying - that remembers the asset and version it came from. Where the
 * server holds no files, the picker says so instead of listing nothing.
 *
 * Several at once (2026-10-20, "a must"): tick them, or Select all here,
 * and they are posted ONE AFTER ANOTHER, so the quota is checked for each;
 * the first refusal stops the rest and its sentence shows in the drawer.
 */
import { useState } from 'react';
import LibraryPicker from './assets/LibraryPicker';
import { NO_FILES } from '../engine/assets/fileKinds';
import type { Handout } from '../engine/handouts';
import type { AssetRow } from '../engine/assets/types';
import type { HandoutLibrary } from '../contexts/HandoutsContext';
import common from '../styles/common.module.css';

interface Props {
  library: HandoutLibrary;
  handouts: readonly Handout[];
  /** The drawer's error line: a refusal (a full session, a file too large) shows there. */
  attempt: (action: () => Promise<unknown>) => Promise<void>;
}

const KINDS = ['handout', 'image'] as const;

/** Each in turn: two posts at once would both pass the quota check that one of them should fail. */
async function postInTurn(library: HandoutLibrary, rows: readonly AssetRow[]): Promise<void> {
  for (const row of rows) await library.pick(row);
}

export default function HandoutFromLibrary({ library, handouts, attempt }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="library-pick-handout"
        onClick={() => setOpen(true)}
      >
        From my library…
      </button>
      {open && (
        <LibraryPicker
          lib={library.lib}
          uid={library.uid}
          kind={KINDS}
          title="From my library · Handouts and images"
          empty={library.filesAvailable ? undefined : NO_FILES}
          action="Copy into the session"
          copies={handouts}
          onClose={() => setOpen(false)}
          onPick={(row) => {
            setOpen(false);
            void attempt(() => library.pick(row));
          }}
          many={{
            label: (n) => (n ? `Copy ${n} into the session` : 'Copy into the session'),
            onPick: (rows) => {
              setOpen(false);
              void attempt(() => postInTurn(library, rows));
            },
          }}
        />
      )}
    </>
  );
}
